import { useModal } from '@ebay/nice-modal-react'
import { Link } from '@tanstack/router'
import Autoplay from 'embla-carousel-autoplay'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import affiliate_icon from '../../assets/profile/affiliate_icon.svg'
import commission_icon from '../../assets/profile/commission_icon.svg'
import free_bonus from '../../assets/profile/free_bonus.svg'
import inbox_icon from '../../assets/profile/inbox_icon.svg'
import logout_icon from '../../assets/profile/logout_icon.svg'
import referral_icon from '../../assets/profile/referral_icon.svg'
import { useEmblaCarouselWithIndex } from '../../hooks/useEmblaCarouselWithIndex'
import useGeoCheck from '../../hooks/useGeoCheck'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import type { PromotionObj } from '../../types/promotion'
import { api } from '../../utils/api-client'
import classNames from '../../utils/classNames'
import { kebabCase } from '../../utils/string'
import LogInModal from '../common/log-in-modal'
import LogoutModal from '../common/log-out-modal'
import Cashier from './cashier'

type FooterProps = {
  closeProfileDrawer: () => void
}

const Footer = ({ closeProfileDrawer }: FooterProps) => {
  const { emblaRef, emblaApi, selectedIndex } = useEmblaCarouselWithIndex(
    { loop: true },
    [Autoplay()],
  )
  const is_agent = useBoundStore(state => state.user.is_agent)
  const username = useBoundStore(state => state.user.username)
  const { geoCheck } = useGeoCheck()
  const logOutModal = useModal(LogoutModal)
  const loginModal = useModal(LogInModal)
  const { t } = useTranslation('drawer')

  const TABS = [
    // {
    //   img: verification_icon,
    //   label: t('verification'),
    //   key: 'verification',
    // },
    // {
    //   img: personal_icon,
    //   label: t('personal-data'),
    //   link: '/profile',
    //   key: 'personal-data',
    // },
    {
      img: referral_icon,
      label: t('referral'),
      link: '/profile/referral',
      key: 'referral',
    },
    {
      img: free_bonus,
      label: t('free-bonus'),
      link: '/profile/free-bonus',
      key: 'free-bonus',
    },
    {
      img: inbox_icon,
      label: t('inbox'),
      link: '/profile/inbox',
      key: 'inbox',
    },
    {
      img: affiliate_icon,
      label: t('affiliate'),
      link: '/profile/affiliate',
      key: 'affiliate',
    },
    {
      img: commission_icon,
      label: t('commission'),
      link: '/profile/commission',
      key: 'commission',
    },
    {
      img: logout_icon,
      label: t('log-out'),
      key: 'logout',
    },
  ]

  const [promoList, setPromoList] = useState<PromotionObj[]>([])
  const [noOfUnread, setNoOfUnread] = useState(0)

  useEffect(() => {
    if (!username) return
    const controller = new AbortController()
    const signal = controller.signal

    const fetchNumberOfUnreadMessages = async () => {
      const response = await api.post<ApiResponse<string>>(
        '/ajax_data.php',
        {
          type: 'noread_message',
        },
        { signal },
      )
      const { status, info } = response.data
      if (status === 1) {
        setNoOfUnread(parseInt(info))
      }
    }
    fetchNumberOfUnreadMessages()

    return () => {
      controller.abort()
    }
  }, [username])

  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal

    const getPromotions = async () => {
      const response = await api.post<ApiResponse<PromotionObj[]>>(
        '/ajax_check.php',
        {
          type: 'get_promotion_list',
        },
        { signal },
      )

      const { status, info } = response.data
      if (status === 1) {
        setPromoList(info)
      }
    }
    getPromotions()

    return () => {
      controller.abort()
    }
  }, [username])

  const onClick = (key: (typeof TABS)[number]['key']) => {
    if (key === 'logout') {
      closeProfileDrawer()
      logOutModal.show()
    }
    return undefined
  }

  const renderTab = (item: (typeof TABS)[number], index: number) => {
    if (!username && item.key === 'logout') return null
    if (
      (is_agent === '0' && item.key === 'affiliate') ||
      (!username && item.key === 'affiliate')
    )
      return null
    if (
      (is_agent === '0' && item.key === 'commission') ||
      (!username && item.key === 'commission')
    )
      return null

    if (item.link) {
      return (
        <Link
          key={index}
          to={username ? item.link : undefined}
          params={{}}
          search={{}}
          onClick={() => {
            closeProfileDrawer()
            if (!username) {
              geoCheck(loginModal.show)
            }
          }}>
          <div className="flx-str-ctr px-5 py-3" key={index}>
            <div className="flx-str-ctr w-10 relative">
              <img
                className="max-w-[25px] max-h-[25px]"
                src={item.img}
                alt={item.label}
              />
              {item.key === 'inbox' && noOfUnread > 0 && (
                <p className="flx-ctr absolute text-[7px] w-3 h-3 bg-[#eb4243] text-[white] text-center rounded-md left-[19px] -top-1">
                  {noOfUnread}
                </p>
              )}
            </div>
            <span className="text-xs font-Syncopate-Bold uppercase text-txt">
              {item.label}
            </span>
          </div>
        </Link>
      )
    }
    return (
      <div
        className="flx-str-ctr px-5 py-3"
        key={index}
        onClick={() => onClick(item.key)}>
        <div className="flx-str-ctr w-10 relative">
          <img
            className="max-w-[25px] max-h-[25px]"
            src={item.img}
            alt={item.label}
          />
        </div>
        <span className="text-xs font-Syncopate-Bold uppercase text-txt">
          {item.label}
        </span>
      </div>
    )
  }

  return (
    <div className="px-0 py-[13px]">
      <div className="relative w-full aspect-[2] mb-[3px] px-5 py-0">
        <div className="absolute z-[8] flex right-[25px] top-2">
          {promoList.map((_item, index) => (
            <div
              key={index}
              onClick={() => emblaApi?.scrollTo(index)}
              className={classNames(
                'w-[5px] h-[5px] border transition-all duration-[0.2s] ease-linear mx-0.5 my-0 rounded-[5px] border-solid border-[white]',
                index === selectedIndex && 'bg-white',
              )}
            />
          ))}
        </div>
        {promoList.length > 0 && (
          <div className="embla" ref={emblaRef}>
            <div className="embla__container">
              {promoList.map((item, index) => (
                <div key={index} className="embla__slide">
                  <Link
                    to="/promotions/$promoId"
                    params={{
                      promoId: `${item.id}-${kebabCase(item.title)}`,
                    }}>
                    <button
                      className="w-full rounded overflow-hidden"
                      onClick={closeProfileDrawer}>
                      <img
                        className="block object-cover w-full"
                        src={item.bannerurl}
                        alt="Banner"
                      />
                    </button>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <Cashier closeProfileDrawer={closeProfileDrawer} />
      {TABS.map(renderTab)}
    </div>
  )
}

export default Footer
